import React, { useState, useEffect } from "react";
import "./partByPartSearch.css";
import ScrollableComponent from "../ScrollableComponent";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  hasDbEditorUpdateAccessFromStorage,
} from "../../utils/authAccess";

export default function PartByPartSearch({
  data = [],
  setSelectedTab,
  setModelData,
  setPricingData,
  setPartData,
}) {
  const [searchDesc, setSearchDesc] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [searchDiv, setSearchDiv] = useState("");
  const [searchSrc, setSearchSrc] = useState("");
  const [searchPart, setSearchPart] = useState("");
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditPartModalOpen, setIsEditPartModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [isAddPartModalOpen, setIsAddPartModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [addPartLoading, setAddPartLoading] = useState(false);
  const [newPartNumber, setNewPartNumber] = useState("");
  const [newProductGroup, setNewProductGroup] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [originalDescription, setOriginalDescription] = useState("");
  const [selectedRowForEdit, setSelectedRowForEdit] = useState(null);
  const [addPartDivId, setAddPartDivId] = useState("");
  const [addPartSourceId, setAddPartSourceId] = useState("");
  const [addPartNumber, setAddPartNumber] = useState("");
  const [addPartTitle, setAddPartTitle] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [isNoPartsFoundModalOpen, setIsNoPartsFoundModalOpen] = useState(false);

  const isAdmin = hasDbEditorUpdateAccessFromStorage();
  const username = localStorage.getItem("username") || "DBEditorWeb";

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    let filtered = data;

    if (searchDesc.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.title.toLowerCase().includes(searchDesc.toLowerCase())
      );
    }

    if (searchDiv.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.divId.toLowerCase().includes(searchDiv.toLowerCase())
      );
    }

    if (searchSrc.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.sourceId.toLowerCase().includes(searchSrc.toLowerCase())
      );
    }

    if (searchPart.trim() !== "") {
      filtered = filtered.filter((item) =>
        item.part?.toLowerCase().includes(searchPart.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [searchDesc, searchDiv, searchSrc, searchPart, data]);

  const handleClick = async (row) => {
    const partNumber = row.number;
    const div = row.divId;
    const pls = row.sourceId;
    setLoading(true);
    setError("");

    try {
      const result = await fetchWithAuth({
        method: "GET",
        endpoint: `/part/getModelByPartNumber`,
        params: { partNumber, div, pls },
      });

      if (result && result.length > 0) {
        setModelData(result);
        setSelectedTab("MODELS");
      } else {
        setError("No Model Found.");
      }
    } catch (err) {
      console.error("Model fetch error", err);
      setError("Error fetching models.");
    } finally {
      setLoading(false);
    }
  };


  const handlePricing = () => {
    if (selectedRows.length > 0) {
      setSelectedTab("PRICING");
      setPricingData(selectedRows);
    }
  };

  const handleRowClick = (row, index) => {
    const exists = selectedRows.some(
      (selectedRow) => selectedRow.index === index
    );

    if (exists) {
      setSelectedRows(selectedRows.filter((r) => r.index !== index));
    } else {
      setSelectedRows([...selectedRows, { ...row, index }]);
    }
  };

  const refetchPartData = async () => {
    if (!data || data.length === 0 || !setPartData) {
      return;
    }

    try {
      const partNumber = data[0]?.number || "";
      if (!partNumber) {
        console.error("No part number found in data to refetch");
        return;
      }

      const refreshedData = await fetchWithAuth({
        method: "GET",
        endpoint: "/part/getPartByNumber",
        params: { number: partNumber.trim() },
      });

      if (Array.isArray(refreshedData) && refreshedData.length > 0) {
        setPartData(refreshedData);
      } else {
        setPartData([]);
      }
    } catch (err) {
      console.error("Error refetching part data:", err);
      setError("Failed to refresh data. Please try again.");
    }
  };

  const handleEditPart = () => {
    if (selectedRows.length === 0) {
      setError("Please select a part to edit.");
    } else if (selectedRows.length === 1) {
      setIsEditPartModalOpen(true);
      setError("");
      const selectedRow = selectedRows[0];
      const description = selectedRow.title || selectedRow.partDescription || selectedRow.partTitle || "";

      setNewPartNumber(selectedRow.number || "");
      setNewProductGroup(selectedRow.divId || "");
      setNewDescription(description);
      setOriginalDescription(description);
      setSelectedRowForEdit(selectedRow);
    } else {
      setError("Please select only one part to edit.");
    }
  };

  const handleSavePart = async () => {
    setError("");
    setModalLoading(true);

    try {
      if (!selectedRowForEdit) {
        setError("No part selected for editing.");
        setModalLoading(false);
        return;
      }

      const hsin = selectedRowForEdit.hsin || "";
      const sourceId = selectedRowForEdit.sourceId || "";

      const payload = {
        hsin: hsin.trim(),
        divId: newProductGroup.trim(),
        sourceId: sourceId.trim(),
        number: newPartNumber.trim(),
        title: newDescription.trim(),
      };

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/part/updatePartByNumber`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      setIsEditPartModalOpen(false);
      setError("");
      setSelectedRowForEdit(null);
      setSelectedRows([]);
      
      // Refetch data to get updated information
      await refetchPartData();
    } catch (err) {
      console.error("Error updating part:", err);
      setError(err.response?.data?.message || "Failed to update part. Please try again.");
    } finally {
      setModalLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditPartModalOpen(false);
    setError("");
    setNewPartNumber("");
    setNewProductGroup("");
    setNewDescription("");
    setOriginalDescription("");
    setSelectedRowForEdit(null);
  };

  const hasChanges = () => {
    const currentDescription = newDescription || "";
    return currentDescription.trim() !== (originalDescription || "").trim();
  };

  const handleDeletePart = () => {
    if (selectedRows.length === 0) {
      setError("Please select at least one part to delete.");
    } else {
      setIsDeleteConfirmModalOpen(true);
      setError("");
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    setError("");

    try {
      const deletePayload = selectedRows.map((row) => {
        const divId = row.divId || "";
        const sourceId = row.sourceId || "";
        const number = row.number || "";
        const hsin = row.hsin || "";

        return {
          hsin: hsin,
          divId: divId,
          sourceId: sourceId,
          number: number
        };
      });

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/part/deletePartByNumber`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: deletePayload,
      });

      setIsDeleteConfirmModalOpen(false);
      setSelectedRows([]);
      setError("");
      
      if (data && data.length > 0 && setPartData) {
        const partNumber = data[0]?.number || "";
        if (partNumber) {
          try {
            const refreshedData = await fetchWithAuth({
              method: "GET",
              endpoint: "/part/getPartByNumber",
              params: { number: partNumber.trim() },
            });

            if (Array.isArray(refreshedData) && refreshedData.length > 0) {
              setPartData(refreshedData);
            } else {
              setPartData([]);
              setIsNoPartsFoundModalOpen(true);
            }
          } catch (refetchErr) {
            console.error("Error refetching part data:", refetchErr);
            setPartData([]);
            setIsNoPartsFoundModalOpen(true);
          }
        } else {
          setIsNoPartsFoundModalOpen(true);
        }
      } else {
        setIsNoPartsFoundModalOpen(true);
      }
    } catch (err) {
      console.error("Error deleting parts:", err);
      setError(err.response?.data?.message || "Failed to delete parts. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmModalOpen(false);
    setError("");
  };

  const fetchDivisions = async () => {
    setLoadingDivisions(true);
    try {
      const divisionsData = await fetchWithAuth({
        method: "GET",
        endpoint: "/divisions/getAllActiveDivision",
      });
      
      let divisionIds = [];
      
      if (Array.isArray(divisionsData)) {
        divisionIds = divisionsData
          .map((div) => {
            if (typeof div === 'object' && div !== null) {
              return div.id || div.divisionId || div.value || null;
            }
            return div; 
          })
          .filter((id) => id !== null && id !== undefined && id !== '');
      } else if (divisionsData && typeof divisionsData === 'object') {
        const values = Object.values(divisionsData).flat();
        divisionIds = values
          .map((div) => {
            if (typeof div === 'object' && div !== null) {
              return div.id || div.divisionId || div.value || null;
            }
            return div;
          })
          .filter((id) => id !== null && id !== undefined && id !== '');
      }
      
      setDivisions(divisionIds.sort());
    } catch (err) {
      console.error("Error fetching divisions:", err);
      setError("Failed to load divisions. Please try again.");
    } finally {
      setLoadingDivisions(false);
    }
  };

  const handleAddPart = async () => {
    setIsAddPartModalOpen(true);
    setError("");
    const searchedPartNumber = data && data.length > 0 ? (data[0]?.number || "") : "";
    setAddPartDivId("");
    setAddPartSourceId("");
    setAddPartNumber(searchedPartNumber);
    setAddPartTitle("");
    await fetchDivisions();
  };

  const handleSaveAddPart = async () => {
    setError("");
    setAddPartLoading(true);

    try {
      if (!addPartDivId.trim() || !addPartNumber.trim() || !addPartTitle.trim()) {
        setError("Please fill in all required fields (Div, Part Number, and Description).");
        setAddPartLoading(false);
        return;
      }

      if (addPartSourceId.trim() && addPartSourceId.trim().length !== 3) {
        setError("Source ID must be exactly 3 characters if provided.");
        setAddPartLoading(false);
        return;
      }

      const payload = {
        divId: addPartDivId.trim(),
        sourceId: addPartSourceId.trim() || "",
        number: addPartNumber.trim(),
        title: addPartTitle.trim(),
      };

      try {
        await fetchWithAuth({
          method: "PUT",
          endpoint: `/part/addPartByNumber`,
          params: { username: username },
          headers: { "Content-Type": "application/json" },
          data: payload,
        });
      } catch (addPartErr) {
        console.error("Error adding part via addPartByNumber:", addPartErr);
        
        let errorMessage = "Failed to add part. Please try again.";
        
        if (addPartErr.response?.data) {
          const errorData = addPartErr.response.data;
          
          if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = typeof errorData.error === 'string' 
              ? errorData.error 
              : errorData.error.message || JSON.stringify(errorData.error);
          } else if (errorData.errorMessage) {
            errorMessage = errorData.errorMessage;
          } else if (errorData.msg) {
            errorMessage = errorData.msg;
          } else if (typeof errorData === 'object') {
            const stringValues = Object.values(errorData).filter(v => typeof v === 'string' && v.trim());
            if (stringValues.length > 0) {
              errorMessage = stringValues[0];
            } else {
              errorMessage = JSON.stringify(errorData);
            }
          }
        } else if (addPartErr.message) {
          errorMessage = addPartErr.message;
        }
        
        setError(errorMessage);
        setAddPartLoading(false);
        return;
      }

      setIsAddPartModalOpen(false);
      setError("");
      
      const partNumberToRefetch = addPartNumber.trim();
      
      setAddPartDivId("");
      setAddPartSourceId("");
      setAddPartNumber("");
      setAddPartTitle("");
      
      if (partNumberToRefetch && setPartData) {
        try {
          const refreshedData = await fetchWithAuth({
            method: "GET",
            endpoint: "/part/getPartByNumber",
            params: { number: partNumberToRefetch },
          });

          if (Array.isArray(refreshedData) && refreshedData.length > 0) {
            setPartData(refreshedData);
          } else {
            setPartData([]);
          }
        } catch (refetchErr) {
          console.error("Error refetching part data:", refetchErr);
        }
      }
    } catch (err) {
      console.error("Unexpected error in handleSaveAddPart:", err);
      setError(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setAddPartLoading(false);
    }
  };

  const handleCancelAddPart = () => {
    setIsAddPartModalOpen(false);
    setError("");
    setAddPartDivId("");
    setAddPartSourceId("");
    setAddPartNumber("");
    setAddPartTitle("");
  };

  const handleCloseNoPartsFoundModal = () => {
    setIsNoPartsFoundModalOpen(false);
    if (setSelectedTab) {
      setSelectedTab("SEARCH");
    }
  };

  return (
    <div className="mainPageContent">
      <ScrollableComponent height="500px">
        <table className="part" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  disabled
                  aria-label="Select all disabled"
                />
              </th>
              <th>Div</th>
              <th>Pls</th>
              <th>Part Number</th>
              <th>Description</th>
            </tr>
            <tr>
              <td />
              <td>
                <input
                  type="text"
                  placeholder="Search Div"
                  className="partSearchInput"
                  value={searchDiv}
                  onChange={(e) => setSearchDiv(e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Search Pls"
                  className="partSearchInput"
                  value={searchSrc}
                  onChange={(e) => setSearchSrc(e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Search Part"
                  className="partSearchInput"
                  value={searchPart}
                  onChange={(e) => setSearchPart(e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  placeholder="Search Description"
                  className="partSearchInput"
                  value={searchDesc}
                  onChange={(e) => setSearchDesc(e.target.value)}
                />
              </td>
            </tr>
          </thead>
          <tbody>
            {filteredData &&
              filteredData.length !== 0 &&
              filteredData.map((row, index) => {
                const isSelected = selectedRows.some(
                  (selectedRow) => selectedRow.index === index
                );
                return (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRowClick(row, index)}
                      />
                    </td>
                  <td>{row.divId}</td>
                  <td>{row.sourceId}</td>
                  <td
                    className="clickable-text"
                    onClick={() => handleClick(row)}
                  >
                    {" "}
                    <b>{row.number} </b>
                  </td>
                  <td>{row.title}</td>
                </tr>
                );
              })}
          </tbody>
        </table>
      </ScrollableComponent>
      <div className="buttonPartGroup">
        <table className="buttonTable" style={{ width: "100%" }}>
          <thead>
            <tr>
              {isAdmin && (
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleAddPart}
                  >
                    <AddIcon /> Add Part
                  </button>
                </td>
              )}
              {isAdmin && (
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleEditPart}
                    disabled={selectedRows.length !== 1}
                  >
                    <EditIcon /> Update Part
                  </button>
                </td>
              )}
              {isAdmin && (
                <td>
                  <button
                    className="btn btn-info"
                    onClick={handleDeletePart}
                    disabled={selectedRows.length === 0}
                  >
                    <DeleteIcon /> Delete Part
                  </button>
                </td>
              )}
              <td>
                <button
                  className="btn btn-info"
                  onClick={handlePricing}
                  disabled={selectedRows.length === 0}
                  aria-disabled={selectedRows.length === 0}
                >
                  <MonetizationOnIcon /> Pricing
                </button>
              </td>
            </tr>
          </thead>
        </table>
      </div>

      {loading && (
        <>
          <div className="blur-background"></div>
          <div className="loading"></div>
        </>
      )}

      {error && !isEditPartModalOpen && !isDeleteConfirmModalOpen && !isAddPartModalOpen && (
        <div className="popupOverlay error">
          <div className="popupContent">
            <p>{error}</p>
            <button onClick={() => setError("")}>Close</button>
          </div>
        </div>
      )}

      {isDeleteConfirmModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Confirm Delete</h3>
            {error && <p className="errorText">{error}</p>}
            <p>
              Are you sure you want to delete {selectedRows.length} part{selectedRows.length > 1 ? 's' : ''}?
            </p>
            {selectedRows.length > 0 && (
              <div style={{ marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
                <strong>Parts to be deleted:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {selectedRows.map((row, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>
                      {row.number || row.partNumber}
                      {row.divId ? ` (Div: ${row.divId})` : ''}
                      {row.sourceId ? ` (PLS: ${row.sourceId})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
              This action cannot be undone.
            </p>
            <div className="modalActions">
              <button onClick={handleConfirmDelete} disabled={deleteLoading} style={{ backgroundColor: '#dc3545', color: 'white' }}>
                {deleteLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
              <button onClick={handleCancelDelete} disabled={deleteLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditPartModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Edit Part</h3>
            {error && <p className="errorText">{error}</p>}

            <label>
              Div:
              <input
                type="text"
                value={newProductGroup}
                onChange={(e) => setNewProductGroup(e.target.value)}
                disabled={true}
                placeholder="Div"
              />
            </label>

            <label>
              Pls:
              <input
                type="text"
                value={selectedRowForEdit?.sourceId || ""}
                disabled={true}
                placeholder="Pls"
              />
            </label>

            <label>
              Part Number:
              <input
                type="text"
                value={newPartNumber}
                onChange={(e) => setNewPartNumber(e.target.value)}
                disabled={true}
                placeholder="Part Number"
              />
            </label>

            <label>
              Description:
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                disabled={modalLoading}
                placeholder="Enter description"
              />
            </label>

            <div className="modalActions">
              <button onClick={handleSavePart} disabled={modalLoading || !hasChanges()}>
                {modalLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
              <button onClick={handleCancelEdit} disabled={modalLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddPartModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Add Part</h3>
            {error && <p className="errorText">{error}</p>}

            <label>
              Div ID: <span style={{ color: "red" }}>*</span>
              {loadingDivisions ? (
                <div style={{ padding: "8px", color: "#666" }}>Loading divisions...</div>
              ) : (
                <select
                  value={addPartDivId}
                  onChange={(e) => setAddPartDivId(e.target.value)}
                  disabled={addPartLoading}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select Div ID</option>
                  {divisions.map((divId) => (
                    <option key={divId} value={divId}>
                      {divId}
                    </option>
                  ))}
                </select>
              )}
            </label>

            <label>
              Source ID:
              <input
                type="text"
                value={addPartSourceId}
                onChange={(e) => setAddPartSourceId(e.target.value)}
                disabled={addPartLoading}
                placeholder="Enter Source ID (optional)"
                maxLength={3}
              />
            </label>

            <label>
              Part Number: <span style={{ color: "red" }}>*</span>
              <input
                type="text"
                value={addPartNumber}
                onChange={(e) => setAddPartNumber(e.target.value)}
                disabled={addPartLoading}
                placeholder="Part Number"
              />
            </label>

            <label>
              Description: <span style={{ color: "red" }}>*</span>
              <input
                type="text"
                value={addPartTitle}
                onChange={(e) => setAddPartTitle(e.target.value)}
                disabled={addPartLoading}
                placeholder="Enter Description"
              />
            </label>

            <div className="modalActions">
              <button onClick={handleSaveAddPart} disabled={addPartLoading}>
                {addPartLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Adding...
                  </>
                ) : (
                  "Add"
                )}
              </button>
              <button onClick={handleCancelAddPart} disabled={addPartLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isNoPartsFoundModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>No Parts Found</h3>
            <p>
              All parts have been deleted. You will be redirected to the Search screen.
            </p>
            <div className="modalActions">
              <button onClick={handleCloseNoPartsFoundModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
